import { useState, useEffect, useRef } from 'react'
import { Loader2, Download, Github, FolderUp } from 'lucide-react'
import {
  createImportFromURL,
  createImportFromGitHub,
  getGitHubLinkStatus,
  type GitHubRepo,
} from '../lib/api'
import GitHubRepoBrowser from './GitHubRepoBrowser'
import FolderDropZone from './FolderDropZone'

// =============================================================================
// URL Validation
// =============================================================================

const ALLOWED_HOSTS = ['github.com', 'gitlab.com', 'bitbucket.org']

function isValidImportURL(url: string): boolean {
  if (!url.startsWith('https://')) return false
  try {
    const parsed = new URL(url)
    return ALLOWED_HOSTS.includes(parsed.hostname.toLowerCase())
  } catch {
    return false
  }
}

// =============================================================================
// Name Extraction & Validation
// =============================================================================

function extractRepoName(url: string): string {
  try {
    const parsed = new URL(url)
    const lastSegment = parsed.pathname.split('/').filter(Boolean).pop() || ''
    // Strip .git suffix, lowercase, replace non-alphanumeric with hyphens, trim hyphens
    return lastSegment
      .replace(/\.git$/, '')
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
  } catch {
    return ''
  }
}

function extractDisplayName(url: string): string {
  try {
    const parsed = new URL(url)
    const lastSegment = parsed.pathname.split('/').filter(Boolean).pop() || ''
    return lastSegment.replace(/\.git$/, '')
  } catch {
    return ''
  }
}

const NAME_PATTERN = /^[a-z][a-z0-9-]*$/

function isValidAppName(name: string): boolean {
  if (name.length < 3 || name.length > 50) return false
  if (!NAME_PATTERN.test(name)) return false
  if (name.includes('--')) return false
  if (name.endsWith('-')) return false
  return true
}

// =============================================================================
// Component
// =============================================================================

type ImportSource = 'url' | 'github' | 'upload'

interface ImportProjectModalProps {
  onClose: () => void
  onImported: (appName: string, importId: number) => void
}

export default function ImportProjectModal({ onClose, onImported }: ImportProjectModalProps) {
  const [source, setSource] = useState<ImportSource>('url')
  const [githubConnected, setGithubConnected] = useState<boolean | null>(null)
  const [url, setUrl] = useState('')
  const [appName, setAppName] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [nameEdited, setNameEdited] = useState(false)
  const [displayNameEdited, setDisplayNameEdited] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const inFlightRef = useRef(false)

  // Auto-extract name from URL when URL changes (only if user hasn't manually edited)
  useEffect(() => {
    if (url && isValidImportURL(url)) {
      if (!nameEdited) {
        setAppName(extractRepoName(url))
      }
      if (!displayNameEdited) {
        setDisplayName(extractDisplayName(url))
      }
    }
  }, [url, nameEdited, displayNameEdited])

  // Check GitHub connection on mount
  useEffect(() => {
    getGitHubLinkStatus().then(status => {
      setGithubConnected(status.linked)
    }).catch(() => {
      setGithubConnected(false)
    })
  }, [])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!isValidImportURL(url) || !isValidAppName(appName)) return
    if (inFlightRef.current) return
    inFlightRef.current = true

    setSubmitting(true)
    setError(null)

    try {
      const result = await createImportFromURL({
        url,
        name: appName,
        display_name: displayName || undefined,
      })
      onImported(result.app_name, result.import_id)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start import')
    } finally {
      setSubmitting(false)
      inFlightRef.current = false
    }
  }

  async function handleGitHubImport(repo: GitHubRepo, branch: string) {
    if (inFlightRef.current) return
    inFlightRef.current = true
    setSubmitting(true)
    setError(null)

    try {
      const result = await createImportFromGitHub({
        repo_full_name: repo.full_name,
        branch,
      })
      onImported(result.app_name, result.import_id)
      setUrl(`https://github.com/${repo.full_name}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start import')
    } finally {
      setSubmitting(false)
      inFlightRef.current = false
    }
  }

  const urlValid = isValidImportURL(url)
  const nameValid = isValidAppName(appName)
  const canSubmit = urlValid && nameValid && !submitting

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-xl font-semibold mb-6">Import Project</h2>
          {/* Source selector tabs */}
          <div className="flex gap-2 mb-5">
            <button
              type="button"
              onClick={() => setSource('url')}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                source === 'url'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
              }`}
            >
              URL
            </button>
            <button
              type="button"
              onClick={() => setSource('github')}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
                source === 'github'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
              }`}
            >
              <Github className="w-4 h-4" />
              GitHub
              {githubConnected === true && (
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 shrink-0" />
              )}
            </button>
            <button
              type="button"
              onClick={() => setSource('upload')}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
                source === 'upload'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
              }`}
            >
              <FolderUp className="w-4 h-4" />
              Upload
            </button>
          </div>

          {/* URL import form */}
          {source === 'url' && (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* URL Input */}
              <div>
                <label className="block text-sm font-medium mb-1.5">Repository URL *</label>
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://github.com/owner/repo"
                  required
                  autoFocus
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <p className="text-xs text-muted-foreground mt-1.5">
                  {url && !urlValid ? (
                    <span className="text-destructive">
                      URL must be HTTPS and from GitHub, GitLab, or Bitbucket
                    </span>
                  ) : (
                    'Paste a public repository URL from GitHub, GitLab, or Bitbucket'
                  )}
                </p>
              </div>

              {/* App Name Input */}
              <div>
                <label className="block text-sm font-medium mb-1.5">App Name *</label>
                <input
                  type="text"
                  value={appName}
                  onChange={(e) => {
                    setAppName(e.target.value.toLowerCase())
                    setNameEdited(true)
                  }}
                  placeholder="my-project"
                  required
                  minLength={3}
                  maxLength={50}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <p className="text-xs text-muted-foreground mt-1.5">
                  {appName && !nameValid ? (
                    <span className="text-destructive">
                      3-50 characters, starts with a letter, lowercase letters, numbers, and hyphens only
                    </span>
                  ) : appName.length >= 3 ? (
                    <>Your app will be at <code className="bg-secondary px-1 rounded">/a/{appName}/</code></>
                  ) : (
                    'Auto-generated from URL. You can edit it.'
                  )}
                </p>
              </div>

              {/* Display Name Input */}
              <div>
                <label className="block text-sm font-medium mb-1.5">Display Name</label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => {
                    setDisplayName(e.target.value)
                    setDisplayNameEdited(true)
                  }}
                  placeholder="My Project"
                  maxLength={255}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <p className="text-xs text-muted-foreground mt-1.5">
                  Optional human-readable name for the app
                </p>
              </div>

              {error && (
                <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                  {error}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Starting import...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      Import
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* GitHub repo browser */}
          {source === 'github' && (
            <>
              {error && (
                <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm mb-4">
                  {error}
                </div>
              )}
              <GitHubRepoBrowser
                onSelect={(repo, branch) => handleGitHubImport(repo, branch)}
                onCancel={onClose}
                importing={submitting}
              />
            </>
          )}

          {/* Upload (folder drag-and-drop) */}
          {source === 'upload' && (
            <FolderDropZone
              onImported={onImported}
              onCancel={onClose}
            />
          )}
        </div>
      </div>
    </div>
  )
}
